
import { testimonials } from '../../../mocks/en/bookData';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function Testimonials() {
  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const testimonial1Reveal = useScrollReveal({ threshold: 0.1 });
  const testimonial2Reveal = useScrollReveal({ threshold: 0.1 });
  const testimonial3Reveal = useScrollReveal({ threshold: 0.1 });
  const testimonial4Reveal = useScrollReveal({ threshold: 0.1 });
  const testimonial5Reveal = useScrollReveal({ threshold: 0.1 });
  const testimonial6Reveal = useScrollReveal({ threshold: 0.1 });

  const testimonialReveals = [
    testimonial1Reveal,
    testimonial2Reveal,
    testimonial3Reveal,
    testimonial4Reveal,
    testimonial5Reveal,
    testimonial6Reveal,
  ];

  return (
    <section id="testimonials" className="py-32 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-purple-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-200/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div 
          ref={headerReveal.ref}
          className={`text-center mb-20 ${headerReveal.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <div className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
            💬 TESTIMONIALS
          </div>
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6">
            What Readers Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Real people, real transformations
          </p>
        </div>

        {/* Testimonials Masonry Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => {
            const reveal = testimonialReveals[index] || testimonial1Reveal;
            return (
              <div
                key={index}
                ref={reveal.ref}
                className={`group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 relative overflow-hidden ${
                  reveal.isVisible ? `animate-fade-in-up stagger-${index + 1}` : 'opacity-0'
                }`}
                style={{
                  transform: reveal.isVisible ? 'none' : 'translateY(30px)',
                }}
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Quote icon */}
                <div className="absolute top-6 right-6 text-6xl text-purple-100 group-hover:text-purple-200 transition-colors duration-300">
                  <i className="ri-double-quotes-r"></i>
                </div>

                <div className="relative z-10">
                  {/* Avatar with gradient spin animation */}
                  <div className="mb-6 relative">
                    <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-full text-white text-2xl font-bold shadow-lg group-hover:shadow-2xl transition-all duration-300 animate-gradient">
                      {testimonial.name.charAt(0)}
                    </div>
                    {/* Rotating gradient ring */}
                    <div className="absolute inset-0 w-16 h-16 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 blur-md animate-rotate-gradient"></div>
                    </div>
                  </div>

                  {/* Testimonial text */}
                  <p className="text-gray-700 leading-relaxed mb-6 relative">
                    "{testimonial.text}"
                  </p>

                  {/* Author info */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>

                  {/* Star rating */}
                  <div className="flex gap-1 mt-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i
                        key={i}
                        className="ri-star-fill text-amber-400 text-lg"
                        style={{
                          animation: reveal.isVisible
                            ? `bounceIn 0.5s ease-out ${0.1 * i}s forwards`
                            : 'none',
                          opacity: reveal.isVisible ? 1 : 0,
                        }}
                      ></i>
                    ))}
                  </div>
                </div>

                {/* Hover accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 text-lg mb-6">
            Join hundreds of people who have already started their transformation journey
          </p>
          <a
            href="#purchase"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-2xl animate-pulse-glow whitespace-nowrap cursor-pointer group"
          >
            <span>Start Now</span>
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
          </a>
        </div>
      </div>

      <style>{`
        @keyframes rotate-gradient {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .animate-rotate-gradient {
          animation: rotate-gradient 3s linear infinite;
        }
      `}</style>
    </section>
  );
}
